import React, { useEffect, useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { evaluateSchoolSubscription } from '../../lib/subscription';
import { hydrateSessionFromPersistedLogin } from '../../lib/persistedAuth';

const ProtectedRoute = ({ children }) => {
  const [currentUserStr, setCurrentUserStr] = useState(() => sessionStorage.getItem('currentUser'));
  const currentUser = useMemo(() => {
    if (!currentUserStr) return null;
    try {
      return JSON.parse(currentUserStr);
    } catch {
      return null;
    }
  }, [currentUserStr]);

  const [checking, setChecking] = useState(true);
  const [allowed, setAllowed] = useState(false);

  useEffect(() => {
    let cancelled = false;

    async function run() {
      // If we don't have a session user yet, try hydrating from the 1-week persisted login (teacher/student).
      if (!currentUserStr) {
        const { didHydrate } = hydrateSessionFromPersistedLogin();
        if (didHydrate) {
          const nextStr = sessionStorage.getItem('currentUser');
          if (!cancelled) setCurrentUserStr(nextStr);
          // Let the effect rerun with a real currentUser; keep checking=true for now.
          return;
        }
      }

      if (!currentUser) {
        if (!cancelled) {
          setAllowed(false);
          setChecking(false);
        }
        return;
      }

      // Fast-path: if the embedded school object is already unavailable, block immediately.
      const embeddedSchool = currentUser?.schools || null;
      const embeddedEval = embeddedSchool ? evaluateSchoolSubscription(embeddedSchool) : null;
      if (embeddedEval && !embeddedEval.isAccessible) {
        sessionStorage.removeItem('currentUser');
        if (!cancelled) {
          setAllowed(false);
          setChecking(false);
        }
        return;
      }

      // Live check: fetch the latest school subscription fields.
      const schoolId = currentUser.school_id || currentUser?.schools?.id;
      if (!schoolId) {
        if (!cancelled) {
          setAllowed(true);
          setChecking(false);
        }
        return;
      }

      try {
        const { data: school, error } = await supabase
          .from('schools')
          .select('id, subscription_start_date, subscription_end_date, subscription_paid, subscription_warning_days')
          .eq('id', schoolId)
          .single();

        if (error) {
          // If we can't verify, do not hard-block the user (network / RLS / schema mismatch).
          if (!cancelled) {
            setAllowed(true);
            setChecking(false);
          }
          return;
        }

        const sub = evaluateSchoolSubscription(school);
        if (!sub.isAccessible) {
          sessionStorage.removeItem('currentUser');
          if (!cancelled) {
            setAllowed(false);
            setChecking(false);
          }
          return;
        }

        if (!cancelled) {
          setAllowed(true);
          setChecking(false);
        }
      } catch {
        if (!cancelled) {
          setAllowed(true);
          setChecking(false);
        }
      }
    }

    setChecking(true);
    run();
    return () => {
      cancelled = true;
    };
  }, [currentUser, currentUserStr]);

  if (!currentUser) {
    return <Navigate to="/" replace />;
  }

  if (checking) {
    return;
  }

  if (!allowed) {
    return <Navigate to="/?error=account-unavailable" replace />;
  }

  return children;
};

export default ProtectedRoute;
